import { useQuery } from '@tanstack/react-query'
import { Tooltip } from 'antd'
import orderApi from 'api/orderApi'
import resourceApi from 'api/resourceApi'
import customDataTableStyle from 'assets/styles/customDataTableStyle'
import dayjs from 'dayjs'
import ContentContainer from 'general/components/ContentContainer'
import CustomDataTable from 'general/components/CustomDataTable'
import CustomSegmented from 'general/components/CustomSegmented'
import CustomTag from 'general/components/CustomTag'
import ModalUnauthorized from 'general/components/Modal/ModalUnauthorized'
import SelectOptions from 'general/constants/SelectOptions'
import Utils from 'general/utils/Utils'
import { useAppSelector } from 'hooks/useRedux'
import find from 'lodash/find'
import { useCallback, useMemo, useState } from 'react'
import { shallowEqual } from 'react-redux'
import ModalInfoOrder from './components/ModalInfoOrder'
import ResourcePlans from './components/ResourcePlans'
import { renderPaymentMethod, renderPaymentStatus } from './helpers'
import './style.scss'

const tabs = ['Lịch sử cấp tài nguyên', 'Quản lý đơn hàng']

function ResourceManagement() {
  const [currentTab, setCurrentTab] = useState(tabs[0])
  const userFunction = useAppSelector(
    (states) => states?.function?.userFunctions,
  )
  const listFunction = useAppSelector(
    (states) => states?.function?.listFunctions,
  )

  const systemType = useAppSelector((s) => s.auth.activeInvoice, shallowEqual)
  const [infoModalState, setInfoModalState] = useState(false)
  const [showNoRightModal, setShowNoRightModal] = useState(false)
  const [activeRow, setActiveRow] = useState(null)
  // ================== Queries n mutations ==================

  const resourceQuery = useQuery({
    enabled:
      currentTab === tabs[0] && (systemType.invoice || systemType.ticket),
    queryKey: ['resource', systemType.invoice],
    queryFn: () =>
      resourceApi.getByCompanyId({
        invoiceType: systemType.invoice ? 'INVOICE' : 'TICKET',
      }),
  })

  const orderQuery = useQuery({
    enabled: currentTab === tabs[1],
    queryKey: ['order'],
    queryFn: () => orderApi.getByCompanyId(),
  })

  //==================== Memos ============================

  const [isViewRight] = useMemo(() => {
    return Utils.checkFunction(
      [
        {
          listFunctionCode: 'INFO_RESOURCES_VIEW_LIST',
        },
        {
          listFunctionCode: 'INFO_RESOURCES_EDIT',
        },
      ],
      userFunction,
      listFunction,
    )
  }, [userFunction, listFunction])

  const columns = useMemo(() => {
    /** @type {import('react-data-table-component').TableColumn<ReturnResourceData>[]} */
    const resourceData = [
      {
        id: 'stt',
        center: true,
        width: '20',
        name: 'STT',
        selector: (_row, index) => index + 1,
      },
      {
        id: 'createdAt',
        name: 'Ngày cấp',
        center: true,

        format: ({ createdAt }) =>
          dayjs(createdAt).isValid() && dayjs(createdAt).format('L'),
        selector: ({ createdAt }) => createdAt,
      },
      {
        id: 'invoiceType',
        name: 'Gói dịch vụ',
        center: true,
        selector: ({ invoiceType }) =>
          find(SelectOptions.resourceType, { value: invoiceType })?.label,
      },
      {
        id: 'invoicePackage',
        name: 'Loại gói ',
        center: true,
        cell: (row) => {
          return (
            <p
              style={{
                textAlign: 'center',
              }}
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 w-100"
            >
              {row?.packageType == 'DURATION' ? 'Thời hạn' : 'Số lượng'}
            </p>
          )
        },
      },
      // {
      //   id: 'packageName',
      //   name: 'Tên gói',
      //   center: true,
      //   selector: ({ packageName }) => packageName,
      // },
      {
        id: 'countBuy',
        name: 'Tài nguyên ',
        right: true,
        cell: (row) => {
          return (
            <p
              style={{
                textAlign: 'right',
              }}
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 w-100"
            >
              {Utils.formatNumber(row?.purchasedQuantity)}{' '}
              {row?.packageType == 'DURATION'
                ? 'ngày'
                : find(SelectOptions.resourceType, { value: row?.invoiceType })
                    ?.label}
            </p>
          )
        },
      },
      // {
      //   id: 'countUsed',
      //   name: 'Số lượng đã sử dụng',
      //   right: true,
      //   cell: (row) => {
      //     const countUsed = row?.remainingUsageQuantity
      //     return (
      //       <p
      //         style={{
      //           textAlign: 'right',
      //         }}
      //         data-tag="allowRowEvents"
      //         className="text-dark-75 font-weight-normal m-0 text-maxline-3 w-100"
      //       >
      //         {row?.packageType == 'DURATION' ? (
      //           <></>
      //         ) : (
      //           <>
      //             {Utils.formatNumber(countUsed < 0 ? 0 : countUsed)}{' '}
      //             {
      //               find(SelectOptions.resourceType, {
      //                 value: row?.invoiceType,
      //               })?.label
      //             }
      //           </>
      //         )}
      //       </p>
      //     )
      //   },
      // },
      {
        id: 'countRemaining',
        name: 'Tài nguyên còn lại',
        right: true,
        cell: (row) => {
          const countUsed =
            row?.remainingUsageQuantity < 0 ? 0 : row?.remainingUsageQuantity
          return (
            <p
              style={{
                textAlign: 'right',
              }}
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 w-100"
            >
              {row?.packageType == 'DURATION' ? (
                <></>
              ) : (
                <>
                  {Utils.formatNumber(countUsed)}{' '}
                  {
                    find(SelectOptions.resourceType, {
                      value: row?.invoiceType,
                    })?.label
                  }
                </>
              )}
            </p>
          )
        },
      },
      {
        id: 'dayStart',
        name: 'Ngày gói có hiệu lực',
        center: true,
        cell: (row) => {
          return (
            <p
              style={{
                textAlign: 'center',
              }}
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 w-100"
            >
              {row?.packageType == 'DURATION' ? (
                <>{Utils.formatDate(row?.effective_date_start, 'DD-MM-YYYY')}</>
              ) : (
                <></>
              )}
            </p>
          )
        },
      },
      {
        id: 'dayEnd',
        name: 'Ngày hết hạn',
        center: true,
        cell: (row) => {
          return (
            <p
              style={{
                textAlign: 'center',
              }}
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 w-100"
            >
              {row?.packageType == 'DURATION' ? (
                <>{Utils.formatDate(row?.expiration_date, 'DD-MM-YYYY')}</>
              ) : (
                <></>
              )}
            </p>
          )
        },
      },
      {
        id: 'status',
        name: 'Trạng thái gói',
        center: true,
        cell: (row) => {
          const countUsed =
            row?.remainingUsageQuantity < 0 ? 0 : row?.remainingUsageQuantity
          const now = dayjs()
          const expiredTime = dayjs(row?.expiration_date)

          const isExpired = now.isAfter(expiredTime)
          return (
            <p
              style={{
                textAlign: 'center',
              }}
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-normal m-0 text-maxline-3 w-100"
            >
              {row?.packageType == 'DURATION' ? (
                <>
                  {row?.active == 1 ? (
                    <>
                      {isExpired ? (
                        <span style={{ color: '#f64e60' }}>Hết tài nguyên</span>
                      ) : (
                        <span style={{ color: '#52c41a' }}>Đang hoạt động</span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: '#f64e60' }}>Đã hủy</span>
                  )}
                </>
              ) : (
                <>
                  {row?.active == 1 ? (
                    <>
                      {countUsed == 0 ? (
                        <span style={{ color: '#f64e60' }}>Hết tài nguyên</span>
                      ) : (
                        <span style={{ color: '#52c41a' }}>Đang hoạt động</span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: '#f64e60' }}>Đã hủy</span>
                  )}
                </>
              )}
            </p>
          )
        },
      },
    ]
    return resourceData
  }, [])

  const columnsOrder = useMemo(() => {
    /**@type {import('react-data-table-component').TableColumn<ReturnOrderData>[]} */
    const orderData = [
      {
        id: 'stt',
        center: true,
        name: 'STT',
        selector: (_row, index) => index + 1,
      },
      {
        id: 'orderCode',
        name: 'Số đơn hàng',
        selector: ({ orderCode }) => orderCode,
      },
      {
        id: 'dateOrder',
        name: 'Ngày đặt hàng',

        format: ({ dateOrder }) =>
          dayjs(dateOrder).isValid() && dayjs(dateOrder).format('L'),
        selector: ({ dateOrder }) => dateOrder,
      },
      {
        id: 'totalPayment',
        name: 'Tổng tiền thanh toán',
        right: true,

        format: ({ totalPayment }) => Utils.formatNumber(totalPayment),
        selector: ({ totalPayment }) => totalPayment,
      },
      {
        id: 'paymentMethod',
        name: 'Phương thức thanh toán',

        format: ({ paymentMethod }) => (
          <Tooltip title={renderPaymentMethod(paymentMethod)}>
            {renderPaymentMethod(paymentMethod)}
          </Tooltip>
        ),
        selector: ({ paymentMethod }) => renderPaymentMethod(paymentMethod),
      },
      {
        id: 'paymentStatus',
        name: 'Trạng thái thanh toán',
        minWidth: '200px',
        format: ({ paymentStatus }) => (
          <CustomTag
            text={renderPaymentStatus(paymentStatus).text}
            tagProps={{
              className: Utils.cn(
                'text-capitalize badge',
                renderPaymentStatus(paymentStatus).cn,
              ),
            }}
          />
        ),
        selector: ({ paymentStatus }) => renderPaymentStatus(paymentStatus),
      },
      {
        id: 'resourceIssuedStatus',
        name: 'Trạng thái cấp tài nguyên',
      },
    ]
    return orderData
  }, [])

  //==================== function ============================

  const viewDetailOrder = useCallback(
    (row) => {
      if (!isViewRight) {
        setShowNoRightModal(true)
      } else {
        setActiveRow(row)
        setInfoModalState(true)
      }
    },
    [isViewRight],
  )

  return (
    <ContentContainer wrapperClassName="ResourceManagement">
      <ContentContainer.Header titleContent={'Quản lý tài nguyên'} />
      <ContentContainer.Body className={'gap-3'}>
        <ResourcePlans />
        <div className="w-50 mt-5">
          <CustomSegmented
            options={tabs}
            defaultValue={currentTab}
            onChange={(tab) => setCurrentTab(tab)}
            fullWidth
          />
        </div>

        <div className=" mt-5">
          <CustomDataTable
            columns={currentTab === tabs[0] ? columns : columnsOrder}
            scrollY="400px"
            dataSource={
              currentTab === tabs[0] ? resourceQuery.data : orderQuery.data
            }
            isLoading={
              currentTab === tabs[0]
                ? resourceQuery.isLoading
                : orderQuery.isLoading
            }
            isClickable={currentTab === tabs[1]}
            customTableStyle={customDataTableStyle}
            handleDoubleClickRow={viewDetailOrder}
          />
        </div>

        {/* <OrderManagement
          updateResource={updateResource}
          setUpdateResource={setUpdateResource}
          data={orders}
          userFunction={userFunction}
          listFunction={listFunction}
          viewRight={isViewRight}
          editRight={isEditRight}
        /> */}
      </ContentContainer.Body>
      {/* Modals */}
      <ModalInfoOrder
        rowData={activeRow}
        isOpen={infoModalState}
        onOpenChange={setInfoModalState}
      />
      <ModalUnauthorized
        show={showNoRightModal}
        setShow={(status) => setShowNoRightModal(status)}
      />
    </ContentContainer>
  )
}

export default ResourceManagement
